/**
 * Pure display formatters for the evidence progress card family (evidence-progress-spec.md
 * §Magnitud L55 and §Anatomía L31/L70). No Angular dependencies, no side effects — plain
 * functions over the numbers the store's `IEvidenceBracketView` carries. Null sizes/durations
 * format to an empty string so callers can omit the segment entirely (spec: "size omitted
 * when null"). Copy lives in `evidence-progress-copy.ts`; this module only formats numbers.
 */

/** Integer formatter shared by every human-facing number — ungrouped compact card meta. */
const NUMBER_FORMAT = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0, useGrouping: false });

/** One decimal formatter for sub-10 unit magnitudes (`4.2 MB`). */
const TENTHS_FORMAT = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
  useGrouping: false,
});

/** Unit ladder step (bytes per unit) and the ceiling unit of the ladder (GB). */
const UNIT_STEP = 1024;
const UNITS = ["B", "KB", "MB", "GB"] as const;

/**
 * Formats a payload size in bytes (base 1024): plain `B` under a kilobyte, then `KB`/`MB`/`GB`
 * with 0 decimals at magnitudes ≥10 and 1 decimal below (`172 MB`, `4.2 MB` — spec L55). The
 * ladder caps at GB. Null formats to an empty string so the summary omits the segment.
 */
export function formatEvidenceBytes(bytes: number | null): string {
  if (bytes == null) return "";
  let value = bytes;
  let unitIndex = 0;
  while (unitIndex < UNITS.length - 1 && value >= UNIT_STEP) {
    value /= UNIT_STEP;
    unitIndex += 1;
  }
  const magnitude = unitIndex === 0 ? NUMBER_FORMAT : value >= 10 ? NUMBER_FORMAT : TENTHS_FORMAT;
  return `${magnitude.format(value)} ${UNITS[unitIndex]}`;
}

/**
 * Formats a duration in milliseconds per the spec ladder (L55): under one second in plain
 * milliseconds (`820 ms`), under a minute in whole seconds (`27 s`, rounded down — the card
 * never overclaims elapsed effort), and from a minute up as `2 min 31 s`. Null formats to an
 * empty string.
 */
export function formatEvidenceDuration(ms: number | null): string {
  if (ms == null) return "";
  if (ms < 1000) return `${NUMBER_FORMAT.format(ms)} ms`;
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${NUMBER_FORMAT.format(totalSeconds)} s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${NUMBER_FORMAT.format(minutes)} min ${NUMBER_FORMAT.format(seconds)} s`;
}

/**
 * Formats the live header chronometer as `m:ss` rounding DOWN (spec L70). Never renders
 * "0:00" as a standing value: below one full second the clock is empty until 1 s elapses
 * (spec L70 — the stopped-value guard).
 */
export function formatEvidenceClock(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 1) return "";
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}
